import React, { useState } from "react";
import {
  Box,
  Typography,
  Stack,
  Avatar,
  Button,
  Card,
  CircularProgress,
  Modal
} from "@mui/material";
import {
  Add as AddIcon,
  OpenInNew as OpenInNewIcon,
  Groups as GroupsIcon,
  Star as StarIcon,
  AccountBalance as AccountBalanceIcon,
  AssignmentInd as AssignmentIndIcon
} from "@mui/icons-material";
import { removeCoordinator, assignCoordinator, changeCoordinator } from "../api/coordinatorApi";
import { Routes } from "../constant/api_url";
import { notification } from "../utils/messages";
import MemberPickerModal from "./MemberPickerModal";
import RoleDetailDrawer from "./RoleDetailDrawer";

const POST_TYPE_ICONS = {
  1: <StarIcon />,
  2: <StarIcon />,
  3: <AccountBalanceIcon />,
  4: <GroupsIcon />,
  5: <AssignmentIndIcon />
};

export default function CoordinatorSection({ chapterId, postType, postTypeName, onRefresh, onEditRole, stagedRemovedIds = [] }) {
  const roles = postType?.roles || [];
  const holder = postType?.host;

  const [pickerOpen, setPickerOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedRoleIsSelf, setSelectedRoleIsSelf] = useState(false);
  const [detailDrawerOpen, setDetailDrawerOpen] = useState(false);
  const [detailRole, setDetailRole] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [removeConfirmOpen, setRemoveConfirmOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [changePickerOpen, setChangePickerOpen] = useState(false);
  const [changeTarget, setChangeTarget] = useState(null);
  const [changeTargetIsSelf, setChangeTargetIsSelf] = useState(false);
  const [changeTargetRoleId, setChangeTargetRoleId] = useState(null);

  const handleAddClick = (role) => {
    setSelectedRole(role?.id || null);
    setSelectedRoleIsSelf(role?.is_self === 1);
    setPickerOpen(true);
  };

  const handleChangeClick = (coordinator, role) => {
    setChangeTarget(coordinator);
    setChangeTargetIsSelf(role?.is_self === 1);
    setChangeTargetRoleId(role?.id || null);
    setChangePickerOpen(true);
  };

  const handleRemoveClick = (coordinator, role) => {
    setRemoveTarget({ ...coordinator, roleName: role?.name || "", roleIsSelf: role?.is_self === 1 });
    setRemoveConfirmOpen(true);
  };

  const handleConfirmRemove = async () => {
    if (!removeTarget) return;
    try {
      setActionLoading(true);
      const response = await removeCoordinator({ id: removeTarget.id });
      if (response.status === 1) {
        notification({ type: "success", message: "Coordinator removed" })();
        onRefresh?.();
      } else {
        notification({ type: "error", message: response.message || "Failed to remove" })();
      }
    } catch (error) {
      notification({ type: "error", message: "Failed to remove coordinator" })();
    } finally {
      setActionLoading(false);
      setRemoveConfirmOpen(false);
      setRemoveTarget(null);
    }
  };

  const handlePickerConfirm = async (memberIds, selectedMembers) => {
    try {
      setActionLoading(true);
      const response = await assignCoordinator({
        chapter_id: parseInt(chapterId, 10),
        role_id: selectedRole,
        member_ids: memberIds,
        ...(selectedRoleIsSelf && postType?.id != null && { assigned_under_post_type_id: postType.id })
      });

      if (response?.status === 1) {
        const already = response.output?.already_assigned?.length || 0;
        const rejected = response.output?.rejected?.length || 0;
        let msg = response.message || "Coordinators assigned";
        if (already > 0) msg += ` (${already} already assigned)`;
        if (rejected > 0) msg += ` (${rejected} rejected)`;
        notification({ type: "success", message: msg })();
        onRefresh?.();
      } else {
        const msg = response?.message || "Operation failed";
        notification({
          type: "error",
          message:
            msg === "Role already assigned to another member"
              ? `${msg} — remove or change the existing holder instead.`
              : msg
        })();
      }
    } catch (error) {
      notification({ type: "error", message: "Operation failed" })();
    } finally {
      setActionLoading(false);
    }
  };

  const handleChangeConfirm = async (memberIds) => {
    const newMemberId = memberIds?.[0];
    if (!newMemberId || !changeTarget) {
      setChangePickerOpen(false);
      setChangeTarget(null);
      setChangeTargetIsSelf(false);
      setChangeTargetRoleId(null);
      return;
    }
    try {
      setActionLoading(true);
      const response = await changeCoordinator({
        id: changeTarget.id,
        member_id: newMemberId,
        remove_coordinators: false
      });
      if (response.status === 1) {
        notification({ type: "success", message: response.message || "Coordinator changed" })();
        onRefresh?.();
      } else {
        notification({ type: "error", message: response.message || "Failed to change coordinator" })();
      }
    } catch (error) {
      notification({ type: "error", message: "Failed to change coordinator" })();
    } finally {
      setActionLoading(false);
      setChangePickerOpen(false);
      setChangeTarget(null);
      setChangeTargetIsSelf(false);
      setChangeTargetRoleId(null);
    }
  };

  const handleViewResponsibilities = (role) => {
    setDetailRole({ id: role.id, name: role.name });
    setDetailDrawerOpen(true);
  };

  const getPhotoUrl = (photo) => {
    if (photo) return `${Routes.API_HOSTNAME}images/member/${photo}`;
    return null;
  };

  const getAssignedMemberIds = () => {
    const ids = [];
    roles.forEach((role) => {
      (role.member_list || []).forEach((c) => ids.push(c.m_id));
    });
    return ids;
  };

  const getChangeExcludedIds = () => {
    // R4: UNIQUE(chapter_id, role_id, member_id) — same member across
    // different roles is allowed, so exclude only the target role's members
    // (same as Add). Excluding the whole postType hides members like Abhay
    // who are in another role of the same section.
    const role = roles.find((r) => r.id === changeTargetRoleId);
    return (role?.member_list || []).map((c) => c.m_id);
  };

  const getSelectedRoleMemberIds = () => {
    const role = roles.find((r) => r.id === selectedRole);
    return (role?.member_list || []).map((c) => c.m_id);
  };

  const renderCoordinatorTile = (coord, role) => {
    const isAccepted = coord.coordinator_acceptance_status === 1;
    const roleIsSelf = role?.is_self === 1;
    const removalStaged = stagedRemovedIds.includes(coord.id);

    return (
    <Box
      key={coord.id}
      sx={{
        width: 120,
        height: 170,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        p: 1,
        pt: 1.5,
        border: "1px solid",
        borderColor: removalStaged ? "error.main" : "divider",
        borderRadius: 2,
        position: "relative",
        bgcolor: "background.paper",
        opacity: removalStaged ? 0.55 : 1,
        transition: "all 0.15s ease",
        "&:hover": {
          boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)"
        }
      }}
    >
      <Box sx={{ position: "relative", mb: 0.75 }}>
        <Avatar
          src={getPhotoUrl(coord.photo)}
          sx={{
            width: 56,
            height: 56,
            border: "2px solid",
            borderColor: "primary.light"
          }}
        >
          {(coord.m_name || "U").charAt(0).toUpperCase()}
        </Avatar>
        {(coord.m_verify_status === 1 || coord.m_verify_status === 2) && (
          <Box
            component="img"
            src={coord.m_verify_status === 2 ? "/static/icons/verified_blue.png" : "/static/icons/verified.png"}
            alt="Verified"
            sx={{
              width: 18,
              height: 18,
              position: "absolute",
              top: -2,
              right: 0
            }}
          />
        )}
      </Box>

      <Typography
        variant="caption"
        sx={{
          fontWeight: 600,
          color: "text.primary",
          textAlign: "center",
          overflow: "hidden",
          textWrap: "auto !important",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          maxWidth: "100%",
          lineHeight: 1.2
        }}
      >
        {coord.m_name}
      </Typography>

      <Typography
        variant="caption"
        sx={{
          color: "text.secondary",
          textAlign: "center",
          fontSize: "0.6rem",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          maxWidth: "100%",
          lineHeight: 1.2
        }}
      >
        {coord.business_name || "Coordinator"}
      </Typography>

      <Typography
        sx={{
          fontSize: "0.65rem",
          fontWeight: 600,
          color: "text.secondary",
          mt: 0.5,
          lineHeight: 1
        }}
      >
        Responsibilities
      </Typography>
      <Box
        sx={{
          mt: 0.25,
          display: "flex",
          alignItems: "center",
          gap: 0.35,
          px: 0.6,
          py: 0.15,
          borderRadius: 1,
          bgcolor: isAccepted ? "success.lighter" : "error.lighter"
        }}
      >
        <Box
          sx={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            bgcolor: isAccepted ? "success.main" : "error.main",
            flexShrink: 0
          }}
        />
        <Typography
          sx={{
            fontSize: "0.65rem",
            fontWeight: 600,
            color: isAccepted ? "success.dark" : "error.dark",
            lineHeight: 1
          }}
        >
          {isAccepted ? "Accepted" : "Pending"}
        </Typography>
      </Box>
      {removalStaged && (
        <Typography
          sx={{
            mt: 0.5,
            fontSize: "0.6rem",
            fontWeight: 700,
            color: "error.dark",
            bgcolor: "error.lighter",
            px: 0.75,
            py: 0.2,
            borderRadius: 1,
            lineHeight: 1.2
          }}
        >
          Will be removed
        </Typography>
      )}
      {isAccepted && coord.coordinator_acceptance_date && (
        <Typography
          sx={{
            fontSize: "0.55rem",
            color: "text.secondary",
            lineHeight: 1,
            mt: 0.25
          }}
        >
          {(() => {
            const d = new Date(coord.coordinator_acceptance_date);
            const day = String(d.getDate()).padStart(2, "0");
            const month = String(d.getMonth() + 1).padStart(2, "0");
            const year = d.getFullYear();
            let hours = d.getHours();
            const minutes = String(d.getMinutes()).padStart(2, "0");
            const ampm = hours >= 12 ? "PM" : "AM";
            hours = hours % 12 || 12;
            return `${day}-${month}-${year} ${hours}:${minutes} ${ampm}`;
          })()}
        </Typography>
      )}

      <Box
        sx={{
          position: "absolute",
          inset: 0,
          bgcolor: "rgba(0, 0, 0, 0.6)",
          borderRadius: 2,
          opacity: 0,
          display: removalStaged ? "none" : "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 0.75,
          transition: "opacity 0.15s ease",
          "&:hover": { opacity: 1 }
        }}
      >
        {!roleIsSelf && (
          <Button
            size="small"
            onClick={() => handleChangeClick(coord, role)}
            disabled={actionLoading}
            sx={{
              minWidth: 0,
              px: 2,
              py: 0.5,
              bgcolor: "white",
              color: "text.primary",
              fontSize: "0.75rem",
              fontWeight: 700,
              borderRadius: 1,
              textTransform: "none",
              "&:hover": { bgcolor: "primary.main", color: "white" }
            }}
          >
            Change
          </Button>
        )}
        <Button
          size="small"
          onClick={() => handleRemoveClick(coord, role)}
          disabled={actionLoading}
          sx={{
            minWidth: 0,
            px: 2,
            py: 0.5,
            bgcolor: "error.main",
            color: "white",
            fontSize: "0.75rem",
            fontWeight: 700,
            borderRadius: 1,
            textTransform: "none",
            "&:hover": { bgcolor: "error.dark" }
          }}
        >
          Remove
        </Button>
      </Box>
    </Box>
    );
  };

  const renderAddTile = (role) => (
    <Box
      onClick={() => handleAddClick(role)}
      sx={{
        width: 120,
        height: 140,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        p: 1.5,
        border: "2px dashed",
        borderColor: "grey.300",
        borderRadius: 2,
        cursor: "pointer",
        "&:hover": { borderColor: "primary.main", bgcolor: "rgba(28, 71, 102, 0.04)" },
        transition: "all 0.15s ease"
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: "50%",
          bgcolor: "grey.100",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 1,
          "&:hover": { bgcolor: "primary.lighter" }
        }}
      >
        <AddIcon sx={{ color: "text.secondary" }} />
      </Box>
      <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary" }}>
        Add Coordinator
      </Typography>
    </Box>
  );

  const renderRole = (role) => {
    const roleIsSelf = role?.is_self === 1;
    const holderCount = (role.member_list || []).length;
    return (
    <Box key={role.id} sx={{ "& + &": { mt: 3 } }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 1.5 }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="body1" sx={{ fontWeight: 600, color: "text.primary" }}>
            {role.name}
          </Typography>
          {roleIsSelf && (
            <Typography
              variant="caption"
              sx={{
                color: "info.dark",
                bgcolor: "info.lighter",
                px: 0.75,
                py: 0.25,
                borderRadius: 0.75,
                fontWeight: 700
              }}
            >
              Self
            </Typography>
          )}
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              bgcolor: "grey.100",
              px: 0.75,
              py: 0.25,
              borderRadius: 0.75,
              fontWeight: 600
            }}
          >
            {holderCount} coordinator(s)
          </Typography>
        </Stack>
        <Button
          size="small"
          endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
          onClick={() => handleViewResponsibilities(role)}
          sx={{
            fontSize: "0.75rem",
            textTransform: "none",
            color: "primary.main",
            fontWeight: 600,
            "&:hover": { textDecoration: "underline" }
          }}
        >
          View Responsibilities
        </Button>
      </Stack>

      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
        {(role.member_list || []).map((coord) => renderCoordinatorTile(coord, role))}
        {(!roleIsSelf || holderCount === 0) && renderAddTile(role)}
      </Stack>
    </Box>
    );
  };

  return (
    <>
      <Card
        variant="outlined"
        sx={{
          boxShadow: "none",
          borderRadius: 2,
          overflow: "hidden",
          border: "1px solid",
          borderColor: "divider"
        }}
      >
        <Box
          sx={{
            p: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            bgcolor: "grey.100",
            borderBottom: "1px solid",
            borderColor: "divider"
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "8px",
                bgcolor: "info.lighter",
                color: "info.main",
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}
            >
              {POST_TYPE_ICONS[postType?.id] || <GroupsIcon />}
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, color: "text.primary" }}>
                {postTypeName}'s Coordinators
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  fontWeight: 600,
                  fontSize: "0.65rem"
                }}
              >
                {postType?.category}
              </Typography>
            </Box>
          </Stack>
        </Box>

        <Box sx={{ p: 2.5, bgcolor: "background.paper" }}>
          {roles.length === 0 ? (
            <Typography variant="body2" sx={{ color: "text.secondary", textAlign: "center", py: 2 }}>
              No roles configured for this post type.
            </Typography>
          ) : (
            roles.map((role) => renderRole(role))
          )}
        </Box>
      </Card>

      <MemberPickerModal
        open={pickerOpen}
        onClose={() => {
          setPickerOpen(false);
          setSelectedRole(null);
          setSelectedRoleIsSelf(false);
        }}
        onConfirm={handlePickerConfirm}
        chapterId={chapterId}
        currentHolderId={selectedRoleIsSelf ? null : holder?.m_id}
        excludedMemberIds={selectedRoleIsSelf ? [] : getSelectedRoleMemberIds()}
        eligiblePostTypeId={selectedRoleIsSelf ? postType?.id : null}
        eligibleRoleId={selectedRoleIsSelf ? selectedRole : null}
        emptyText={selectedRoleIsSelf ? "Not Assigned" : undefined}
        loading={actionLoading}
        title={selectedRoleIsSelf ? "Assign Self Role" : "Add Coordinators"}
        subtitle={
          selectedRoleIsSelf
            ? "Select one post holder to assign this Self role."
            : `Assign coordinators to ${postTypeName}.`
        }
        single={selectedRoleIsSelf}
        confirmLabel={selectedRoleIsSelf ? "Assign" : "Add"}
      />

      <MemberPickerModal
        open={changePickerOpen}
        onClose={() => {
          setChangePickerOpen(false);
          setChangeTarget(null);
          setChangeTargetIsSelf(false);
          setChangeTargetRoleId(null);
        }}
        onConfirm={handleChangeConfirm}
        chapterId={chapterId}
        currentHolderId={changeTargetIsSelf ? null : holder?.m_id}
        excludedMemberIds={changeTargetIsSelf ? [] : getChangeExcludedIds()}
        eligiblePostTypeId={changeTargetIsSelf ? postType?.id : null}
        eligibleRoleId={changeTargetIsSelf ? changeTargetRoleId : null}
        emptyText={changeTargetIsSelf ? "Not Assigned" : undefined}
        loading={actionLoading}
        title="Change Coordinator"
        subtitle={
          changeTargetIsSelf
            ? "Select one post holder as the new holder for this Self role."
            : `Replace the current coordinator for ${postTypeName}.`
        }
        confirmLabel="Change"
        single
      />

      <RoleDetailDrawer
        open={detailDrawerOpen}
        onClose={() => { setDetailDrawerOpen(false); setDetailRole(null); }}
        roleData={detailRole}
        onEdit={onEditRole}
      />

      <Modal open={removeConfirmOpen} onClose={() => { setRemoveConfirmOpen(false); setRemoveTarget(null); }}>
        <Box sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 400,
          bgcolor: "background.paper",
          borderRadius: 2,
          boxShadow: 24,
          p: 3
        }}>
          <Typography variant="h6" gutterBottom>{removeTarget?.roleIsSelf ? "Remove Self Assignment" : "Remove Coordinator"}</Typography>
          {removeTarget?.roleIsSelf ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Are you sure you want to remove <strong>{removeTarget?.m_name}</strong> from Self role{" "}
              <strong>{removeTarget?.roleName}</strong>? They will need to self-assign again from the member app.
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Are you sure you want to remove <strong>{removeTarget?.m_name}</strong> as coordinator?
            </Typography>
          )}
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button
              variant="outlined"
              onClick={() => { setRemoveConfirmOpen(false); setRemoveTarget(null); }}
              disabled={actionLoading}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleConfirmRemove}
              disabled={actionLoading}
              startIcon={actionLoading ? <CircularProgress size={16} /> : null}
            >
              {actionLoading ? "Removing..." : "Remove"}
            </Button>
          </Stack>
        </Box>
      </Modal>
    </>
  );
}
